import dayjs from "dayjs";

import { supabase } from "~/integrations/supabase/client";
import type {
  AccountMoveLine,
  AccountMovement,
  MoveLineFormData,
  MoveLineInput,
  MovementFilters,
  MovementFormData,
} from "~/types/contabilidad";
import { auditLogService } from "./auditLogService";
import { closedPeriodService } from "./closedPeriodService";

export const movementService = {
  // ============================================
  // MOVEMENTS
  // ============================================

  async getAll(filters?: MovementFilters): Promise<AccountMovement[]> {
    let query = supabase
      .from("account_movements")
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*),
        supplier_payment:supplier_payments(id, is_reversed)
      `
      )
      .eq("is_deleted", false);

    if (filters?.company_id) {
      query = query.eq("company_id", filters.company_id);
    }

    if (filters?.journal_id) {
      query = query.eq("journal_id", filters.journal_id);
    }

    if (filters?.is_published !== undefined) {
      query = query.eq("is_published", filters.is_published);
    }

    if (filters?.date_from) {
      query = query.gte("move_date", filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte("move_date", filters.date_to);
    }

    if (filters?.search) {
      query = query.ilike("ref", `%${filters.search}%`);
    }

    const { data, error } = await query.order("move_date", {
      ascending: false,
    });

    if (error) throw error;
    return data as AccountMovement[];
  },

  async getByCompany(companyId: string): Promise<AccountMovement[]> {
    const { data, error } = await supabase
      .from("account_movements")
      .select(
        `
        *,
        journal:journal_id(*),
        supplier_payment:supplier_payments(id, is_reversed)
      `
      )
      .eq("company_id", companyId)
      .eq("is_deleted", false)
      .order("move_date", { ascending: false });

    if (error) throw error;
    return data as AccountMovement[];
  },

  async getById(id: string): Promise<AccountMovement> {
    const { data, error } = await supabase
      .from("account_movements")
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*),
        supplier_payment:supplier_payments(id, is_reversed)
      `
      )
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as AccountMovement;
  },

  async getWithLines(id: string): Promise<AccountMovement> {
    const { data: movement, error: movementError } = await supabase
      .from("account_movements")
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*),
        supplier_payment:supplier_payments(id, is_reversed)
      `
      )
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (movementError) throw movementError;

    const { data: lines, error: linesError } = await supabase
      .from("account_move_line")
      .select(
        `
        *,
        account:account_id(*)
      `
      )
      .eq("move_id", id)
      .order("created_at", { ascending: true });

    if (linesError) throw linesError;

    return {
      ...movement,
      lines,
    } as AccountMovement;
  },

  async create(
    formData: MovementFormData,
    userRole?: string
  ): Promise<AccountMovement> {
    // Extract lines from formData
    const { lines, ...movementData } = formData;

    // Check closed period for non-admin users
    if (userRole && userRole !== "superadmin" && userRole !== "admin") {
      const moveDate = dayjs(movementData.move_date);
      const isClosed = await closedPeriodService.isMonthClosed(
        moveDate.year(),
        moveDate.month() + 1
      );
      if (isClosed) {
        throw new Error(
          "El periodo contable está cerrado. Solo administradores pueden registrar en este mes."
        );
      }
    }

    // Generate move_number based on move_date (accounting period)
    const { data: moveNumberResult, error: moveNumberError } = await supabase
      .rpc("generate_move_number", { target_date: movementData.move_date });

    if (moveNumberError) throw moveNumberError;

    // Create the movement with generated move_number
    const { data, error } = await supabase
      .from("account_movements")
      .insert({
        ...movementData,
        move_number: moveNumberResult,
      })
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*)
      `
      )
      .single();

    if (error) throw error;

    // If there are lines, create them
    if (lines && lines.length > 0) {
      const linesWithMoveId: MoveLineFormData[] = lines.map((line) => ({
        ...line,
        move_id: data.id,
      }));

      const { error: linesError } = await supabase
        .from("account_move_line")
        .insert(linesWithMoveId);

      if (linesError) throw linesError;
    }

    return data as AccountMovement;
  },

  async update(
    id: string,
    formData: Partial<MovementFormData>,
    userRole?: string
  ): Promise<AccountMovement> {
    // Extract lines from formData - lines are stored in a separate table
    const { lines, ...movementData } = formData;

    // If updating move_date, check closed period for non-admin users
    if (
      movementData.move_date &&
      userRole &&
      userRole !== "superadmin" &&
      userRole !== "admin"
    ) {
      const moveDate = dayjs(movementData.move_date);
      const isClosed = await closedPeriodService.isMonthClosed(
        moveDate.year(),
        moveDate.month() + 1
      );
      if (isClosed) {
        throw new Error(
          "El periodo contable está cerrado. Solo administradores pueden modificar en este mes."
        );
      }
    }

    // Update the movement
    const { data, error } = await supabase
      .from("account_movements")
      .update(movementData)
      .eq("id", id)
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*)
      `
      )
      .single();

    if (error) throw error;

    // If lines are provided, delete existing lines and recreate them
    if (lines && lines.length > 0) {
      // Delete existing lines
      const { error: deleteError } = await supabase
        .from("account_move_line")
        .delete()
        .eq("move_id", id);

      if (deleteError) throw deleteError;

      // Create new lines
      const linesWithMoveId: MoveLineFormData[] = lines.map((line) => ({
        ...line,
        move_id: id,
      }));

      const { error: linesError } = await supabase
        .from("account_move_line")
        .insert(linesWithMoveId);

      if (linesError) throw linesError;
    }

    return data as AccountMovement;
  },

  // Update for pago-a-proveedor drafts: the header plus ONLY the non-AP lines.
  // The Proveedores (cuenta por pagar, código 2103·) lines are owned by the AP
  // subledger — rewriting them would diverge supplier_invoices.saldo_clp from the
  // GL, so they are left untouched (a DB trigger also blocks editing/deleting them).
  // `lines` carries the desired set of NON-AP lines; they fully replace the existing
  // non-AP lines. Any AP line accidentally present in `lines` is dropped on insert
  // (the guard does not cover INSERT, so a stray AP line would create drift).
  async updatePreservingApLines(
    id: string,
    formData: Partial<MovementFormData>,
    userRole?: string
  ): Promise<AccountMovement> {
    const { lines, ...movementData } = formData;

    // If updating move_date, check closed period for non-admin users
    if (
      movementData.move_date &&
      userRole &&
      userRole !== "superadmin" &&
      userRole !== "admin"
    ) {
      const moveDate = dayjs(movementData.move_date);
      const isClosed = await closedPeriodService.isMonthClosed(
        moveDate.year(),
        moveDate.month() + 1
      );
      if (isClosed) {
        throw new Error(
          "El periodo contable está cerrado. Solo administradores pueden modificar en este mes."
        );
      }
    }

    // Update the header
    const { data, error } = await supabase
      .from("account_movements")
      .update(movementData)
      .eq("id", id)
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*)
      `
      )
      .single();

    if (error) throw error;

    if (lines) {
      const isApCode = (code?: string | null) =>
        (code ?? "").replace(/\D/g, "").startsWith("2103");

      // PostgREST returns the embedded account as an object for a to-one relation,
      // but the generated types can widen it to an array — normalize both shapes.
      const relCode = (account: unknown): string | null => {
        const rel = Array.isArray(account) ? account[0] : account;
        return (rel as { code?: string } | null)?.code ?? null;
      };

      // Existing lines + their account codes, to tell AP from non-AP.
      const { data: existing, error: existingError } = await supabase
        .from("account_move_line")
        .select("id, account:account_id(code)")
        .eq("move_id", id);

      if (existingError) throw existingError;

      const nonApExistingIds = (existing ?? [])
        .filter((line) => !isApCode(relCode(line.account as unknown)))
        .map((line) => line.id);

      // Delete ONLY the non-AP existing lines; the AP lines stay untouched.
      if (nonApExistingIds.length > 0) {
        const { error: deleteError } = await supabase
          .from("account_move_line")
          .delete()
          .in("id", nonApExistingIds);

        if (deleteError) throw deleteError;
      }

      // Resolve codes for the submitted lines so we never re-insert an AP line.
      const submittedAccountIds = [...new Set(lines.map((l) => l.account_id))];
      const codeByAccountId = new Map<string, string>();
      if (submittedAccountIds.length > 0) {
        const { data: accounts, error: accountsError } = await supabase
          .from("account")
          .select("id, code")
          .in("id", submittedAccountIds);

        if (accountsError) throw accountsError;
        for (const account of accounts ?? []) {
          codeByAccountId.set(account.id, account.code);
        }
      }

      const insertLines: MoveLineFormData[] = lines
        .filter((line) => !isApCode(codeByAccountId.get(line.account_id)))
        .map((line) => ({ ...line, move_id: id }));

      if (insertLines.length > 0) {
        const { error: insertError } = await supabase
          .from("account_move_line")
          .insert(insertLines);

        if (insertError) throw insertError;
      }
    }

    return data as AccountMovement;
  },

  async publish(id: string, userId: string): Promise<AccountMovement> {
    // First, validate that the movement is balanced
    const { data: lines, error: linesError } = await supabase
      .from("account_move_line")
      .select("debit, credit")
      .eq("move_id", id);

    if (linesError) throw linesError;

    const totalDebit =
      lines?.reduce((sum, line) => sum + Number(line.debit), 0) ?? 0;
    const totalCredit =
      lines?.reduce((sum, line) => sum + Number(line.credit), 0) ?? 0;

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      throw new Error(
        `Asiento descuadrado. Diferencia: ${(totalDebit - totalCredit).toFixed(2)}`
      );
    }

    if (lines?.length < 2) {
      throw new Error("El asiento debe tener al menos 2 líneas");
    }

    const { data, error } = await supabase
      .from("account_movements")
      .update({ is_published: true })
      .eq("id", id)
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*)
      `
      )
      .single();

    if (error) throw error;

    // Log the publish action
    await auditLogService.log({
      movementId: id,
      moveNumber: data.move_number,
      action: "publish",
      performedBy: userId,
      moveDate: data.move_date,
      companyId: data.company_id,
    });

    return data as AccountMovement;
  },

  async unpublish(
    id: string,
    userId: string,
    userRole: string
  ): Promise<AccountMovement> {
    // Only admin users can unpublish
    if (userRole !== "superadmin" && userRole !== "admin") {
      throw new Error(
        "Solo administradores pueden despublicar comprobantes contables."
      );
    }

    const { data, error } = await supabase
      .from("account_movements")
      .update({ is_published: false })
      .eq("id", id)
      .select(
        `
        *,
        company:company_id(*),
        journal:journal_id(*)
      `
      )
      .single();

    if (error) throw error;

    // Log the unpublish action
    await auditLogService.log({
      movementId: id,
      moveNumber: data.move_number,
      action: "unpublish",
      performedBy: userId,
      moveDate: data.move_date,
      companyId: data.company_id,
    });

    return data as AccountMovement;
  },

  async delete(id: string): Promise<void> {
    // Soft delete using RPC function to bypass RLS
    // (SELECT policy hides deleted rows, which conflicts with setting is_deleted=true)
    const { error } = await supabase.rpc("soft_delete_account_movement", {
      movement_id: id,
    });

    if (error) throw error;
  },

  // ============================================
  // MOVEMENT LINES
  // ============================================

  async getLines(moveId: string): Promise<AccountMoveLine[]> {
    const { data, error } = await supabase
      .from("account_move_line")
      .select(
        `
        *,
        account:account_id(*)
      `
      )
      .eq("move_id", moveId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return data as AccountMoveLine[];
  },

  async createLine(formData: MoveLineFormData): Promise<AccountMoveLine> {
    const { data, error } = await supabase
      .from("account_move_line")
      .insert(formData)
      .select(
        `
        *,
        account:account_id(*)
      `
      )
      .single();

    if (error) throw error;
    return data as AccountMoveLine;
  },

  async updateLine(
    id: string,
    formData: Partial<MoveLineFormData>
  ): Promise<AccountMoveLine> {
    const { data, error } = await supabase
      .from("account_move_line")
      .update(formData)
      .eq("id", id)
      .select(
        `
        *,
        account:account_id(*)
      `
      )
      .single();

    if (error) throw error;
    return data as AccountMoveLine;
  },

  async deleteLine(id: string): Promise<void> {
    const { error } = await supabase
      .from("account_move_line")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  async createLines(lines: MoveLineFormData[]): Promise<AccountMoveLine[]> {
    const { data, error } = await supabase
      .from("account_move_line")
      .insert(lines).select(`
        *,
        account:account_id(*)
      `);

    if (error) throw error;
    return data as AccountMoveLine[];
  },
};
